import { useInfiniteQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { VirtualizedTable } from '@/shared/components/table/VirtualizedTable'
import { Button } from '@/shared/components/ui/button'
import {
  eventResponseToSignal,
  eventsService,
  timeRangeToFromDate,
} from '../services/eventsService'
import type {
  Signal,
  SignalFilters as SignalFiltersType,
} from '../types/events.types'
import { SignalActionMenu } from './SignalActionMenu'
import { SignalDetailsDrawer } from './SignalDetailsDrawer'
import { SignalFilters } from './SignalFilters'

export function SignalsExploreTab() {
  const [selectedSignal, setSelectedSignal] = useState<Signal | null>(null)
  const [filters, setFilters] = useState<SignalFiltersType>({
    time_range: 'last_24h',
  })
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const fromDate = timeRangeToFromDate(filters.time_range, filters.custom_from)

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['events', filters],
    queryFn: ({ pageParam = 1 }) =>
      eventsService.getEvents({
        page: pageParam as number,
        limit: 50,
        event_name: filters.signal_name || undefined,
        customer_id: filters.customer_id || undefined,
        from_date: fromDate,
        to_date: filters.custom_to || undefined,
        sort: 'timestamp',
        order: 'desc',
      }),
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.pagination.has_next ? lastPage.pagination.page + 1 : undefined,
    staleTime: 30 * 1000,
  })

  const signals =
    data?.pages.flatMap(page => page.data.map(eventResponseToSignal)) ?? []
  const total = data?.pages[0]?.pagination.total ?? 0

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const handleRowClick = (signal: Signal) => {
    setSelectedSignal(signal)
    setIsDrawerOpen(true)
  }

  const columns: ColumnDef<Signal>[] = [
    {
      accessorKey: 'signal_name',
      header: 'Event Name',
      cell: ({ row }) => (
        <div className='text-sm'>
          <div className='font-medium'>{row.original.signal_name}</div>
        </div>
      ),
      size: 370,
    },
    {
      id: 'customer_id',
      header: 'Customer ID',
      cell: ({ row }) => (
        <div className='text-sm'>
          <div className='font-medium font-mono text-xs'>
            {row.original.customer_id || '-'}
          </div>
        </div>
      ),
      size: 250,
    },
    {
      id: 'agent_id',
      header: 'Agent ID',
      cell: ({ row }) => (
        <div className='text-sm'>
          {row.original.agent_id ? (
            <div className='font-medium font-mono text-xs'>
              {row.original.agent_id}
            </div>
          ) : (
            <div className='text-xs text-muted-foreground'>-</div>
          )}
        </div>
      ),
      size: 225,
    },
    {
      accessorKey: 'signal_type',
      header: 'Signal Type',
      cell: ({ row }) => (
        <div className='text-sm'>
          <div className='font-medium'>
            {row.original.signal_type === 'cost_signal'
              ? 'Cost Signal'
              : 'Billable Signal'}
          </div>
        </div>
      ),
      size: 180,
    },
    {
      accessorKey: 'timestamp',
      header: 'Timestamp',
      cell: ({ row }) => (
        <div className='text-sm'>
          <div className='font-medium'>
            {formatDateTime(row.original.timestamp)}
          </div>
          <div className='text-xs text-muted-foreground'>
            {new Date(row.original.timestamp).toLocaleDateString()}
          </div>
        </div>
      ),
      size: 225,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <SignalActionMenu
          signal={row.original}
          onViewDetails={handleRowClick}
        />
      ),
      size: 60,
    },
  ]

  return (
    <div className='space-y-6'>
      <SignalFilters filters={filters} onFiltersChange={setFilters} />

      <div className='flex justify-between items-center'>
        <div className='text-sm text-muted-foreground'>
          {total} event{total !== 1 ? 's' : ''} found
        </div>
        <Button
          variant='outline'
          size='sm'
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw
            className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
        <VirtualizedTable
          data={signals}
          columns={columns}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={!!hasNextPage}
          fetchNextPage={fetchNextPage}
          containerHeight='calc(100vh - 460px)'
          emptyMessage='No events found. Events will appear here when your client SDKs send data.'
          loadingMessage='Loading more events...'
          onRowClick={handleRowClick}
          enableSorting={true}
          enableColumnResizing={true}
          enableColumnReordering={true}
          enableColumnPinning={false}
        />
      </div>

      <SignalDetailsDrawer
        signal={selectedSignal}
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        onViewDetails={handleRowClick}
      />
    </div>
  )
}
